import type { BoxProps } from '@mui/material/Box';
import type { Breakpoint } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {CONFIG} from "@/config-global";
import {RouterLink} from "@/routes/components";
import {varAlpha, bgGradient} from "@/theme/styles";

// ----------------------------------------------------------------------

type SectionProps = BoxProps & {
  title?: string;
  method?: string;
  imgUrl?: string;
  subtitle?: string;
  layoutQuery: Breakpoint;
  methods?: {
    path: string;
    icon: string;
    label: string;
  }[];
};

export function Section({
  sx,
  method,
  layoutQuery,
  methods,
  title = 'Digitizing Retail Chain Distribution',
  imgUrl = '/v2/images/auth-modern.png',
  subtitle = 'Connect brands, distributors, retailers, and field teams through one intelligent route-to-market platform built for Africa.',
  ...other
}: SectionProps) {
  const theme = useTheme();

  return (
    <Box
      sx={{
        ...bgGradient({
          color: `0deg, ${varAlpha(theme.vars.palette.background.defaultChannel, 0.92)}, ${varAlpha(theme.vars.palette.background.defaultChannel, 0.92)}`,
          imgUrl: `${CONFIG.assetsDir}/assets/background/background-3-blur.webp`,
        }),
        px: { md: 4, lg: 5 },
        py: 4,
        width: 1,
        maxWidth: 560,
        display: 'none',
        position: 'relative',
        pt: 'var(--layout-header-desktop-height)',
        [theme.breakpoints.up(layoutQuery)]: {
          gap: 5,
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          justifyContent: 'center',
        },
        ...sx,
      }}
      {...other}
    >
      <Box sx={{ maxWidth: 440, textAlign: 'center' }}>
        <Chip
          label="Africa's intelligent route to market"
          size="small"
          color="primary"
          variant="soft"
          sx={{ mb: 2.5 }}
        />
        <Typography variant="h3" sx={{ textWrap: 'balance' }}>
          {title}
        </Typography>

        {subtitle && (
          <Typography sx={{ color: 'text.secondary', mt: 2, lineHeight: 1.7 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          width: 1,
          p: 1,
          borderRadius: 2.5,
          bgcolor: 'background.paper',
          border: `1px solid ${varAlpha(theme.vars.palette.grey['500Channel'], 0.16)}`,
          boxShadow: `0 24px 64px ${varAlpha(theme.vars.palette.grey['900Channel'], 0.2)}`,
        }}
      >
        <Box display="flex" gap={0.75} px={0.5} pt={0.25} pb={1}>
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'error.main' }} />
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'warning.main' }} />
          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'success.main' }} />
        </Box>
        <Box
          component="img"
          alt="Hokela route-to-market analytics dashboard"
          src={imgUrl}
          sx={{
            width: 1,
            display: 'block',
            aspectRatio: '16/10',
            objectFit: 'cover',
            objectPosition: 'top',
            borderRadius: 1.5,
          }}
        />
      </Box>

      {!!methods?.length && method && (
        <Box component="ul" gap={2} display="flex">
          {methods.map((option) => {
            const selected = method === option.label.toLowerCase();

            return (
              <Box
                key={option.label}
                component="li"
                sx={{
                  ...(!selected && {
                    cursor: 'not-allowed',
                    filter: 'grayscale(1)',
                  }),
                }}
              >
                <Tooltip title={option.label} placement="top">
                  <Link
                    component={RouterLink}
                    href={option.path}
                    sx={{
                      ...(!selected && { pointerEvents: 'none' }),
                    }}
                  >
                    <Box
                      component="img"
                      alt={option.label}
                      src={option.icon}
                      sx={{ width: 32, height: 32 }}
                    />
                  </Link>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
